import type { PatientCardSortOrder } from '@/contexts/screenConfiguration.types';

export const DEFAULT_PATIENT_CARD_SORT_ORDER: PatientCardSortOrder = 'DEFAULT';

type PatientCardProcedure = {
  startTime?: string | null;
  startTimeText?: string | null;
  endTimeText?: string | null;
  patientAge?: string | number | null;
  patientSex?: string | null;
};

type PatientCardPatient = {
  id?: string | number | null;
  changedRoomAt?: string | null;
  procedure?: PatientCardProcedure | string | null;
  procedureData?: PatientCardProcedure | null;
};

const normalizeDisplayValue = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;

  const normalized = String(value).trim();
  return normalized && normalized !== '-' && normalized !== '–' ? normalized : null;
};

const getPatientCardProcedure = (patient: PatientCardPatient | null | undefined): PatientCardProcedure | null => {
  if (!patient) return null;
  if (patient.procedureData) return patient.procedureData;
  return patient.procedure && typeof patient.procedure === 'object' ? patient.procedure : null;
};

export const formatPatientCardAgeAndSex = (patient: PatientCardPatient | null | undefined): string => {
  const procedure = getPatientCardProcedure(patient);
  return [normalizeDisplayValue(procedure?.patientAge), normalizeDisplayValue(procedure?.patientSex)]
    .filter(Boolean)
    .join(' ');
};

export const getPatientCardProcedureTimeParts = (
  patient: PatientCardPatient | null | undefined
): { startTime: string | null; endTime: string | null } => {
  const procedure = getPatientCardProcedure(patient);
  return {
    startTime: normalizeDisplayValue(procedure?.startTimeText),
    endTime: normalizeDisplayValue(procedure?.endTimeText),
  };
};

const timestamp = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

const compareNullableTimestamps = (left: number | null, right: number | null): number => {
  if (left === null && right === null) return 0;
  if (left === null) return 1;
  if (right === null) return -1;
  return left - right;
};

const compareRoomEntryThenId = (left: PatientCardPatient, right: PatientCardPatient): number => {
  const roomEntryComparison = compareNullableTimestamps(timestamp(left.changedRoomAt), timestamp(right.changedRoomAt));
  if (roomEntryComparison !== 0) return roomEntryComparison;

  return String(left.id ?? '').localeCompare(String(right.id ?? ''), undefined, { numeric: true });
};

export const sortPatientCards = <T extends PatientCardPatient>(
  patients: T[],
  order: PatientCardSortOrder | null | undefined
): T[] => {
  if (order !== 'SCHEDULED_START_ASC') return patients;

  return [...patients].sort((left, right) => {
    const scheduledStartComparison = compareNullableTimestamps(
      timestamp(getPatientCardProcedure(left)?.startTime),
      timestamp(getPatientCardProcedure(right)?.startTime)
    );

    return scheduledStartComparison || compareRoomEntryThenId(left, right);
  });
};
